/* global process */
// ── GLOBAL CONVERSATION V2: the conversation as a chronological list of TOPICS ──────────────────
//
//   ○ Best hair dryer                      Oct 12 / 10:10 AM
//     Question and Busy response
//   ○ Best egg cooker                      Oct 12 / 10:25 AM
//     Web search and Busy response
//       ✓ Research saved
//         Ongoing: Best egg cooker
//         Research Pack: Duronic
//     View Ongoing · View Research Pack
//
// A topic is an unbroken run of turns in one Home session (busy-timeline-srv `v2/chat/topics`).
// Global Conversation records what was asked and, under it, what the user saved or created from it;
// an Ongoing LINKS to the topic and the original is never moved, copied or archived.
//
// Pure, like every other rail builder (lib/chatRail, lib/entityTimeline): the flag, the wording and
// what the wire's answers mean. The fetches are store/modules/chatTopicsV2.js's, the watching is
// lib/chatTopicsV2Plugin.js's, the drawing is components/GlobalConversationV2/.
//
// Nothing here makes data up: a field the server did not send is a line that is not drawn.

import { LIVE_ROW_ID, CHAT_RAIL_TEXT, liveTurns, railStamp } from '@/lib/chatRail'
import { homeConversation } from '@/lib/chatSession'
import { parseUtc } from '@/lib/dueStatus'

// ── The flag ─────────────────────────────────────────────────────────────────────────────────────

/** Is this env value a yes? Unset, empty and anything unrecognised are all off. */
export const flagOn = (value) => /^(1|true|on|yes)$/i.test(String(value ?? '').trim())

/**
 * The whole feature's one switch, read here and nowhere else. Off is today's app.
 *
 * The app is built by Vue CLI (webpack), which hands client code only `VUE_APP_*` variables, so the
 * build sets `VUE_APP_GLOBAL_CONVERSATION_V2`. `VITE_GLOBAL_CONVERSATION_V2` is the feature's
 * name in the spec and is honoured for a runner that passes it through (vitest does).
 */
// The image is the same `build-live` bundle on dev and prod, so a build variable alone cannot mean
// "dev only". The dev hosts are the one place V2 is on without one; prod's hostname never matches.
export const isDevHost = (hostname) => ['dev.busy.me', 'www.dev.busy.me', 'my.dev.busy.me'].includes(String(hostname ?? '').toLowerCase())

export const GLOBAL_CONVERSATION_V2 = flagOn(
  process.env.VUE_APP_GLOBAL_CONVERSATION_V2 || process.env.VITE_GLOBAL_CONVERSATION_V2,
) || isDevHost(globalThis.location?.hostname)

// ── Words and sizes ──────────────────────────────────────────────────────────────────────────────

export const GC_V2_TEXT = {
  railTitle: 'Global Conversation Timeline',
  untitled: 'Conversation',
  question: 'Question and Busy response',
  webSearch: 'Web search and Busy response',
  researchSaved: 'Research saved',
  linked: 'Linked',
  unlink: 'Remove link',
  viewOngoing: 'View Ongoing',
  viewPack: 'View Research Pack',
  merge: 'Merge with previous',
  split: 'Split here',
  splitShow: 'Split',
  splitHide: 'Hide',
  actionFailed: 'Could not change this conversation.',
  // The save card (PDF p2).
  suggestedMatch: 'Suggested match',
  ongoing: 'Ongoing',
  pack: 'Research Pack',
  chooseAnother: 'Choose another',
  saveHere: 'Save here',
  saveConversation: 'Save conversation',
  chooseDestination: 'Choose a destination',
  finding: 'Finding where this belongs…',
  stickyReason: 'Where you saved the last thing from this conversation.',
  choosePlaceholder: 'Search, or name a new Ongoing...',
  close: 'Close',
  saveFailed: 'Could not save this.',
  linkFailed: 'Saved, but this conversation could not be linked.',
  open: 'Open',
  // The Ongoing's Briefing and Timeline (PDF p6, p7).
  fromGlobal: 'From Global Conversation',
  withinOngoing: 'Within this Ongoing',
  continueHere: 'Continue the conversation here…',
  viewOriginal: 'View original conversation',
  timelineLine: 'Conversation from Global Conversation',
  viewInGlobal: 'View in Global Conversation',
  empty: 'No conversations here.',
  loading: 'Loading conversations…',
}

/** How many topics one page of the rail asks for. */
export const TOPICS_PAGE = 30
/** How many turns the deep link brings back around the turn it points at. */
export const AROUND_TAKE = 50
/** The expiry hint only shows inside this window. */
export const EXPIRY_WINDOW_MS = 3 * 24 * 60 * 60 * 1000
/** The query parameter of the deep link: `/home?gcMessage=<messageId>`. */
export const DEEP_LINK_QUERY = 'gcMessage'
/** A topic in `chatSessions`' viewing slot is keyed like this, so it cannot collide with a session. */
const TOPIC_KEY = 'topic:'

const clean = (v) => String(v ?? '').replace(/\s+/g, ' ').trim()
const list = (v) => (Array.isArray(v) ? v.filter(Boolean) : [])
const first = (...values) => {
  const found = values.find((v) => v !== null && v !== undefined && v !== '')
  return found === undefined ? null : found
}
const ms = (v) => {
  if (v === null || v === undefined || v === '') return NaN
  return parseUtc(v).getTime()
}

export const unwrap = (res) => res?.data?.data ?? res?.data ?? null

export const topicKey = (id) => `${TOPIC_KEY}${id}`
/** The topic id in a `topic:<id>` key, or null for anything else (a session id, `live`). */
export const topicIdOf = (key) => {
  const text = String(key ?? '')
  return text.startsWith(TOPIC_KEY) ? text.slice(TOPIC_KEY.length) || null : null
}

// ── Reading the wire ─────────────────────────────────────────────────────────────────────────────

/**
 * A destination: a topic's `lastDestination`, or a row of `suggest-destination`'s `ongoings[]`, whose
 * own id and title are the Ongoing's (`pack_id` is how busy-intelligent-srv spells it). Null when it
 * names no Ongoing, which is the one thing a destination must have.
 */
export function destinationOf(dto) {
  if (!dto || typeof dto !== 'object') return null
  const ongoingId = first(dto.ongoingId, dto.id)
  if (!ongoingId) return null
  const packId = first(dto.packId, dto.pack_id)
  const confidence = Number(first(dto.confidence, NaN))
  return {
    ongoingId: String(ongoingId),
    ongoingTitle: clean(first(dto.ongoingTitle, dto.title)),
    packId: packId ? String(packId) : null,
    packTitle: clean(first(dto.packTitle, dto.pack_title)),
    reason: clean(dto.reason),
    confidence: Number.isFinite(confidence) ? confidence : null,
    sticky: false,
  }
}

/**
 * `researchpack:<id>@busy-timeline-srv` → `['researchpack', '<id>']`. The service part is dropped:
 * the web writes it and a ref read back may not carry the same one.
 */
function refParts(ref) {
  const body = String(ref ?? '').split('@')[0]
  const colon = body.indexOf(':')
  return colon <= 0 ? [null, null] : [body.slice(0, colon).toLowerCase(), body.slice(colon + 1).toLowerCase()]
}

function savedEntry(dto) {
  const [refType, refId] = refParts(dto?.ref)
  const type = clean(first(dto?.type, refType)).toLowerCase()
  const id = first(dto?.id, refId)
  if (!type || !id) return null
  const count = Number(dto?.count)
  return {
    key: `${type}:${String(id).toLowerCase()}`,
    type,
    id: String(id),
    // Neither is a field of the route today: read when the server sends them (see the final report).
    title: clean(dto?.title),
    count: Number.isFinite(count) && count > 0 ? count : null,
    ongoingId: null,
  }
}

/**
 * What the topic produced: its turns' `saved[]`, joined with what its links say. A link's `savedRef`
 * is the one record of WHICH Ongoing a ref went to, and the only record of a save that no turn was
 * stamped with. A ref is one row however many of these name it.
 */
function savedOf(dto, ongoings) {
  const entries = new Map()
  for (const entry of list(dto.saved).map(savedEntry).filter(Boolean)) entries.set(entry.key, entry)
  for (const link of ongoings) {
    const entry = link.savedRef ? savedEntry({ ref: link.savedRef }) : null
    if (entry) entries.set(entry.key, { ...(entries.get(entry.key) || entry), ongoingId: link.id })
  }
  return [...entries.values()]
}

/** One topic as the server sent it, normalised. Null without an id. */
export function topicOf(dto) {
  if (!dto?.id) return null
  const questionCount = Number(dto.questionCount)
  const ongoings = list(dto.ongoings).filter((o) => o.id).map((o) => ({
    id: String(o.id), title: clean(o.title), savedRef: o.savedRef || null,
  }))
  return {
    id: String(dto.id),
    sessionId: dto.sessionId ? String(dto.sessionId) : null,
    title: clean(dto.title) || GC_V2_TEXT.untitled,
    startedUtc: dto.startedUtc || null,
    lastMessageUtc: dto.lastMessageUtc || null,
    questionCount: Number.isFinite(questionCount) ? questionCount : 0,
    firstMessageId: dto.firstMessageId ? String(dto.firstMessageId) : null,
    ongoings,
    saved: savedOf(dto, ongoings),
    expiresUtc: dto.expiresUtc || null,
    lastDestination: destinationOf(dto.lastDestination),
    // Not a field of the route today: without it the row says the plainer of its two lines.
    webSearch: !!dto.webSearch,
  }
}

/** The topics of a list answer. */
export const topicsFrom = (data) => list(data).map(topicOf).filter(Boolean)

/** The turns of `topics/{id}/messages` (an array) or `topics/around/{id}` (`messages`). */
export const messagesFrom = (data) => list(Array.isArray(data) ? data : data?.messages)

/** The topic `POST sessions/{id}/place` left the session on. */
export const topicIdFromPlace = (data) => (data?.currentTopicId ? String(data.currentTopicId) : null)

/** The topic of `GET topics/around/{messageId}`. */
export const aroundTopic = (data) => topicOf(data?.topic)

/**
 * The ranked destinations of `suggest-destination`: `{ source, ongoings[] }`, `source` being
 * 'sticky' (the topic's last destination), 'matches' or 'none'.
 */
export function matchesFrom(data) {
  const sticky = data?.source === 'sticky'
  return list(data?.ongoings).map(destinationOf).filter(Boolean).map((m) => ({ ...m, sticky }))
}

/** A page of topics fills the rail in place: rows the page covers go, so a merged one does not stay. */
export function mergeNewestPage(known, page) {
  const incoming = list(page)
  if (!incoming.length) return []
  const floor = Math.min(...incoming.map((t) => ms(t.startedUtc)).filter((n) => !Number.isNaN(n)))
  const ids = new Set(incoming.map((t) => t.id))
  const older = list(known).filter((t) => !ids.has(t.id) && ms(t.startedUtc) < floor)
  return [...older, ...incoming]
}

const byStart = (a, b) => (ms(a.startedUtc) || 0) - (ms(b.startedUtc) || 0)

// ── Under a topic: what it produced ──────────────────────────────────────────────────────────────

const CREATED_WORD = { task: 'Task', note: 'Note', event: 'Event', document: 'Document' }

function destinationLines(topic, entry, { ongoingTitleOf, packTitleOf }) {
  const only = topic.ongoings.length === 1 ? topic.ongoings[0] : null
  const ongoing = entry.ongoingId
    ? clean(topic.ongoings.find((o) => o.id === entry.ongoingId)?.title || ongoingTitleOf?.(entry.ongoingId))
    : clean(only?.title)
  const last = topic.lastDestination
  const pack = entry.type === 'researchpack'
    ? clean((last?.packId?.toLowerCase() === entry.id.toLowerCase() ? last.packTitle : '') || packTitleOf?.(entry.id))
    : ''
  return [
    ...(ongoing ? [`${GC_V2_TEXT.ongoing}: ${ongoing}`] : []),
    ...(pack ? [`${GC_V2_TEXT.pack}: ${pack}`] : []),
  ]
}

/**
 * The child rows of a topic, one per thing saved or created from it (PDF p1).
 *
 * The Ongoing line is only drawn when it is certain: a link says which, or the topic has one link.
 * The extractions count and an item's name are the server's to send; without them the row says
 * what kind of thing it was. A Task's Research Pack line waits on the same thing: nothing on the
 * wire ties a Task to a pack.
 */
export function topicChildren(topic, lookups = {}) {
  const rows = []
  for (const entry of topic.saved) {
    const lines = destinationLines(topic, entry, lookups)
    if (entry.type === 'researchpack') {
      const plural = entry.count === 1 ? 'extraction' : 'extractions'
      const title = entry.count ? `${entry.count} ${plural} saved` : GC_V2_TEXT.researchSaved
      rows.push({ key: entry.key, kind: 'saved', icon: 'tick-circle', title, note: '', lines })
    } else if (CREATED_WORD[entry.type]) {
      const note = `${CREATED_WORD[entry.type]} created`
      rows.push({ key: entry.key, kind: 'created', icon: 'task-square', title: entry.title || note, note: entry.title ? note : '', lines })
    }
  }
  return rows
}

/** "View Ongoing · View Research Pack": the routes, or null when the topic has no Ongoing to show. */
export function topicLinks(topic) {
  const sticky = topic.lastDestination
  const ongoing = topic.ongoings.find((o) => o.id === sticky?.ongoingId) || topic.ongoings[topic.ongoings.length - 1]
  if (!ongoing) return null
  const pack = sticky?.ongoingId === ongoing.id ? sticky.packId : null
  const packId = pack || topic.saved.find((s) => s.type === 'researchpack' && (!s.ongoingId || s.ongoingId === ongoing.id))?.id || null
  return {
    ongoing: `/ongoing/${ongoing.id}`,
    pack: packId ? { path: `/ongoing/${ongoing.id}`, query: { pack: packId } } : null,
  }
}

/**
 * The hint under a topic the retention sweep will reach within three days. `expiresUtc` is null for
 * a protected topic, so no hint is ever drawn for one.
 */
export function expiryHint(expiresUtc, now = Date.now()) {
  const left = ms(expiresUtc) - now
  if (Number.isNaN(left) || left >= EXPIRY_WINDOW_MS) return null
  const days = Math.floor(left / (24 * 60 * 60 * 1000))
  if (days <= 0) return 'Expires today'
  return `Expires in ${days} ${days === 1 ? 'day' : 'days'}`
}

// ── The rail ─────────────────────────────────────────────────────────────────────────────────────

/**
 * The live turns the server has not placed in a topic yet. Placing happens after the answer is
 * journalled, so a question just asked can be ahead of the list; a turn older than the session's
 * last placed message is already in it. A turn with no time cannot be told apart, and is left out.
 */
export function unplacedTurns(turns, topics, liveId) {
  const live = liveTurns(turns).filter((t) => clean(t.question) && !t.restored)
  if (!live.length) return []
  const key = liveId ? String(liveId) : null
  const placed = list(topics).filter((t) => key && t.sessionId === key).map((t) => ms(t.lastMessageUtc))
  const known = placed.filter((n) => !Number.isNaN(n))
  const last = known.length ? Math.max(...known) : -Infinity
  return live.filter((t) => ms(t.askedUtc) > last)
}

const rowOf = (topic, previous, { viewingId, lookups, now }) => ({
  id: topic.id,
  key: topicKey(topic.id),
  title: topic.title,
  subtitle: topic.webSearch ? GC_V2_TEXT.webSearch : GC_V2_TEXT.question,
  stamp: railStamp(topic.startedUtc || topic.lastMessageUtc),
  children: topicChildren(topic, lookups),
  links: topicLinks(topic),
  expiry: expiryHint(topic.expiresUtc, now),
  active: !!viewingId && String(viewingId) === topicKey(topic.id),
  live: false,
  placeholder: false,
  canMerge: !!previous && !!topic.sessionId && previous.sessionId === topic.sessionId,
  canSplit: topic.questionCount > 1,
})

/**
 * Every row of the rail, oldest first and the live one last, and whether it ends with
 * "+ New conversation". The same shape and the same "Ready when you are" row as lib/chatRail's
 * `chatRailRows`, so the two rails end alike.
 *
 * @param {object} input
 * @param {Array}  input.topics         the normalised topics
 * @param {Array}  [input.turns]        the turns on screen (timelineSearch.conversation)
 * @param {Array}  [input.stash]        the live turns, while something else is shown in the middle
 * @param {string} [input.viewingId]    what is shown in the middle instead (`topic:<id>`, chatSessions)
 * @param {string} [input.liveId]       the live session's id
 * @param {boolean}[input.pendingBreak] "+ New conversation" was pressed and nothing asked since
 * @param {string} [input.pendingNewUtc] when it was pressed
 * @param {object} [input.lookups]      `{ ongoingTitleOf, packTitleOf }`
 * @param {number} [input.now]
 * @returns {{ rows: Array, showNew: boolean }}
 */
export function topicRailRows({
  topics = [], turns = [], stash = null, viewingId = null, liveId = null,
  pendingBreak = false, pendingNewUtc = null, lookups = {}, now = Date.now(),
} = {}) {
  const sorted = [...list(topics)].sort(byStart)
  const rows = sorted.map((t, i) => rowOf(t, sorted[i - 1], { viewingId, lookups, now }))

  const onScreen = viewingId ? list(stash) : list(turns)
  const liveKey = liveId ? String(liveId) : null
  // The live session's newest topic IS the conversation in the middle: its row goes back to the
  // live turns rather than showing a slice of them. Only while those turns exist in this tab.
  if (liveTurns(onScreen).length && liveKey) {
    const current = [...rows].reverse().find((r) => sorted.find((t) => t.id === r.id)?.sessionId === liveKey)
    if (current) current.live = true
  }

  const unplaced = unplacedTurns(onScreen, sorted, liveId)
  if (unplaced.length) {
    const head = unplaced[0]
    rows.push({
      id: LIVE_ROW_ID,
      key: LIVE_ROW_ID,
      title: clean(head.question),
      subtitle: unplaced.some((t) => t.web) ? GC_V2_TEXT.webSearch : GC_V2_TEXT.question,
      stamp: railStamp(head.askedUtc),
      children: [], links: null, expiry: null, active: false, live: true, placeholder: false,
      canMerge: false, canSplit: false,
    })
    return { rows, showNew: true }
  }
  if (pendingBreak) {
    rows.push({
      id: 'pending', key: 'pending', title: CHAT_RAIL_TEXT.ready, subtitle: CHAT_RAIL_TEXT.newConversation,
      stamp: railStamp(pendingNewUtc), children: [], links: null, expiry: null, active: false, live: true,
      placeholder: true, canMerge: false, canSplit: false,
    })
    return { rows, showNew: false }
  }
  return { rows, showNew: rows.length > 0 }
}

/** The topic's turns as Home draws them, for the rail's split list and the Briefing's reading. */
export const topicTurns = (messages) => homeConversation(messages)

/** The position of the turn a message belongs to; a question and its answer are one turn. */
export function turnIndexOf(turns, messageId) {
  const id = String(messageId ?? '')
  if (!id) return -1
  return list(turns).findIndex((t) => t.id === `chat-${id}` || String(t.messageId) === id)
}

/** The question a selection of ticked research came from, which is what finds its topic. */
export const selectionQuestion = (items) => clean(list(items).find((i) => clean(i.query))?.query)

/** The card's heading while a selection is being saved: "Save 2 selected extractions" (PDF p2). */
export function selectionHeading(count) {
  const n = Math.max(0, Number(count) || 0)
  return `Save ${n} selected ${n === 1 ? 'extraction' : 'extractions'}`
}

export function savedSelectionText(count, title) {
  const n = Math.max(0, Number(count) || 0)
  const head = `${n} ${n === 1 ? 'extraction' : 'extractions'} saved`
  return clean(title) ? `${head} to ${clean(title)}` : head
}

/** The rows the Ongoing's Briefing and Timeline list, in the order they happened (PDF p6, p7). */
export function ongoingTopicRows(topics) {
  return [...list(topics)].sort(byStart).map((t) => ({
    id: t.id,
    title: t.title,
    stamp: railStamp(t.startedUtc || t.lastMessageUtc),
    firstMessageId: t.firstMessageId,
  }))
}

/** Where "View in Global Conversation" goes: the exact turn, not the latest message. */
export const deepLinkRoute = (messageId) => (messageId ? { path: '/home', query: { [DEEP_LINK_QUERY]: String(messageId) } } : null)

/**
 * Bring a turn of the middle into view. Home is drawn by then only sometimes (the link can land from
 * another page, and the thread mounts after it), so it looks again for a moment before giving up.
 * The questions are the one marker every turn has: the Nth question on screen is the Nth turn.
 */
export function scrollToTurn(index, { doc = document, wait = setTimeout, tries = 12, delay = 150 } = {}) {
  const el = doc.querySelectorAll('[data-test="chat-thread"] [data-test="ai-question"]')[index]
  if (el) {
    el.scrollIntoView({ behavior: 'auto', block: 'center' })
    return
  }
  if (tries > 0) wait(() => scrollToTurn(index, { doc, wait, tries: tries - 1, delay }), delay)
}
